/**
 * Printer Setup integration component
 * Handles printer configuration with DIY guides and remote access options
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Printer, Monitor, Wifi, Cable, Smartphone, Laptop, ExternalLink, Bluetooth, AlertTriangle } from 'lucide-react';

import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingSection, SetupMethod, PrinterSetupFormData } from '@/types/onboarding';
import IntegrationSetup from './IntegrationSetup';

// Validation schema for remote access
const printerSetupSchema = z.object({
  printer_setup_method: z.nativeEnum(SetupMethod),
  teamviewer_id: z.string().optional(),
  teamviewer_password: z.string().optional(),
}).refine((data) => {
  if (data.printer_setup_method === SetupMethod.REMOTE_ACCESS) {
    return data.teamviewer_id && data.teamviewer_password;
  }
  return true;
}, {
  message: "TeamViewer ID and password are required for remote access setup",
  path: ["teamviewer_id"],
});

type PrinterSetupFormValues = z.infer<typeof printerSetupSchema>;

const PrinterSetup: React.FC = () => {
  const {
    onboardingData,
    getSectionData,
    getSectionStatus,
    validateSection,
    updateSection,
    markSectionComplete,
    isSaving,
    isMarkingComplete,
  } = useOnboarding();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sectionStatus = getSectionStatus(OnboardingSection.PRINTER);
  const sectionData = useMemo(() => getSectionData(OnboardingSection.PRINTER), [onboardingData]);
  const currentSetupMethod = sectionData?.printer_setup_method || SetupMethod.DIY;

  const form = useForm<PrinterSetupFormValues>({
    resolver: zodResolver(printerSetupSchema),
    defaultValues: {
      printer_setup_method: SetupMethod.DIY,
      teamviewer_id: '',
      teamviewer_password: '',
    },
  });

  // Load existing data
  useEffect(() => {
    if (sectionData) {
      form.reset({
        printer_setup_method: sectionData.printer_setup_method || SetupMethod.DIY,
        teamviewer_id: sectionData.teamviewer_id || '',
        teamviewer_password: sectionData.teamviewer_password || '',
      });
      setHasUnsavedChanges(false);
    }
  }, [sectionData, form.reset]);

  // Watch for form changes
  useEffect(() => {
    const subscription = form.watch(() => {
      setHasUnsavedChanges(true);
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Handle setup method change
  const handleSetupMethodChange = (method: SetupMethod) => {
    form.setValue('printer_setup_method', method);
    setHasUnsavedChanges(true);
  };

  // Save section data
  const handleSave = async () => {
    try {
      const formData = form.getValues();
      await updateSection(OnboardingSection.PRINTER, formData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving printer setup:', error);
    }
  };

  // Mark section as complete
  const handleMarkComplete = async () => {
    try {
      const formData = form.getValues();
      
      // For DIY setup, we don't need to validate the form since no additional data is required
      if (formData.printer_setup_method === SetupMethod.DIY) {
        await updateSection(OnboardingSection.PRINTER, formData);
        await markSectionComplete(OnboardingSection.PRINTER);
        setHasUnsavedChanges(false);
      } else {
        // For remote access, validate the form first
        const isValid = await form.trigger();
        if (isValid) {
          await updateSection(OnboardingSection.PRINTER, formData);
          await markSectionComplete(OnboardingSection.PRINTER);
          setHasUnsavedChanges(false);
        }
      }
    } catch (error) {
      console.error('Error marking printer setup complete:', error);
    }
  };

  // Check if can mark complete
  const canMarkComplete = () => {
    const values = form.getValues();
    if (values.printer_setup_method === SetupMethod.REMOTE_ACCESS) {
      return !!(values.teamviewer_id && values.teamviewer_password);
    }
    return true; // DIY can always be marked complete
  };

  // Custom DIY content with subtabs
  const customDiyContent = (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
        <Printer className="h-5 w-5 text-blue-600 mt-1" />
        <div>
          <h4 className="font-medium text-blue-900">DIY Printer Setup</h4>
          <p className="text-sm text-blue-700">
            Choose the setup method that matches your restaurant's infrastructure and needs.
          </p>
        </div>
      </div>

      <Tabs defaultValue="pos" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pos" className="flex items-center gap-2 data-[state=active]:text-white group">
            <Laptop className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
            POS Printer Installation
          </TabsTrigger>
          <TabsTrigger value="tablet" className="flex items-center gap-2 data-[state=active]:text-white group">
            <Smartphone className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
            Tablet Installation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pos" className="space-y-6 mt-6">
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border">
              <h5 className="font-medium text-green-900 mb-2">Best for:</h5>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Restaurants with existing Windows based POS systems</li>
                <li>• 80mm Epson Thermal Printers</li>
                <li>• Stable Ethernet/USB Connections</li>
              </ul>
            </div>

            <Tabs defaultValue="windows-shared" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="windows-shared" className="flex items-center gap-2 data-[state=active]:text-white group">
                  <Monitor className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                  Windows Shared Printing (Recommended)
                </TabsTrigger>
                <TabsTrigger value="network" className="flex items-center gap-2 data-[state=active]:text-white group">
                  <Wifi className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                  Network/Ethernet
                </TabsTrigger>
                <TabsTrigger value="usb" className="flex items-center gap-2 data-[state=active]:text-white group">
                  <Cable className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                  USB Connection
                </TabsTrigger>
              </TabsList>

              <TabsContent value="windows-shared" className="space-y-4 mt-6">
                <div className="space-y-4">
                  <h4 className="font-medium">Windows Shared Printing Setup Steps:</h4>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="step-1">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 1: Download the PushPrinter Application on your POS device</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              <strong>If you're accessing this dashboard from the POS:</strong>
                              <div className="mt-2">
                                <a 
                                  href="https://pushprinter-v3-releases.s3.amazonaws.com/PushPrinter+Setup+3.0.0.exe" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Download PushPrinter
                                </a>
                              </div>
                            </li>
                            <li>
                              <strong>Accessing this dashboard from another device?</strong> Simply open up a web browser on the POS device and navigate to <a href="https://pushprinter.com/#windows" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://pushprinter.com/#windows</a>. Then, click 'Download'
                            </li>
                            <li>Once the application is downloaded, open 'downloads' in the web browser and click on the downloaded file. Click 'Allow application to make changes to this device' and complete the installation.</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 2: Enable Windows shared printing</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-2 text-sm">
                            <li>1. Open Windows Control Panel from the windows search bar</li>
                            <li>2. Open 'Devices and Printers'</li>
                            <li>3. Right click the printer you want to connect to and select 'Printer Properties'</li>
                            <li>4. To test that the correct printer is selected, press 'Print Test Page'. (If the test page does not print, please reach out to our team for help - support@pumpd.co.nz)</li>
                            <li>5. Toggle the 'Share this printer' checkbox to 'Yes' and click 'Apply'</li>
                            <li>6. Note the name of the printer for later use</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 3: Setup the virtual printer on the Pump'd Admin Dashboard</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              1. Navigate to your admin dashboard here:
                              <div className="mt-2">
                                <a 
                                  href="https://admin.pumpd.co.nz" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Admin Dashboard
                                </a>
                              </div>
                            </li>
                            <li>2. Login with the Temporary Password sent to you by your account manager</li>
                            <li>3. Navigate to Settings → System → Receipt Printing and click 'Create Printer'</li>
                            <li>4. Give the printer a name like "Receipts" or "Kitchen Dockets"</li>
                            <li>5. Toggle 'Auto Print Orders' or 'Auto Print Orders (Confirmed Only) to 'On'</li>
                            <li>6. Scroll down to click 'Save'</li>
                            <li>7. Click the API key to copy it to the clipboard and note it down for later use.</li>
                          </ol>
                          <div className="bg-blue-50 p-3 rounded border mt-3">
                            <p className="text-sm text-blue-700">
                              <strong>Note:</strong> Accessing this dashboard from a different device than the POS? Send yourself an email with the API key to ensure it is copied correctly in the next step
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-4">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 4: Connect the printer to Pump'd by configuring the PushPrinter App</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-2 text-sm">
                            <li>1. Open the PushPrinter App (Previously downloaded and installed)</li>
                            <li>2. Navigate to 'Settings' and enable 'Automatically start Pushprinter' to ensure the application starts when the POS is turned off or restarted</li>
                            <li>3. Navigate to 'Printers' and click 'Create Printer'</li>
                            <li>4. Give the printer the same name as you named the virtual printer on Pump'd Admin (Not necessary but good for keeping track)</li>
                            <li>5. Copy and paste your API key from the Pump'd Admin settings</li>
                            <li>6. Ensure the Printing Type is 'Windows Shared Printer (ESCPOS)'</li>
                            <li>7. Add the Windows Shared Printer Name exactly as it is named in the windows shared printer settings <Badge variant="destructive" className="ml-2">Important</Badge></li>
                            <li>8. Optionally modify the Number Of Copies</li>
                            <li>9. Click 'Send Test Print' to check the device is configured correctly</li>
                            <li>10. Click 'Edit Printer' to save</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </TabsContent>

              <TabsContent value="network" className="space-y-4 mt-6">
                <div className="space-y-4">
                  <h4 className="font-medium">Network Setup Steps:</h4>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="step-1">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 1: Identify the printer's IP address</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-2 text-sm">
                            <li>1. Switch printer power to 'off'</li>
                            <li>2. Hold test print button (While device is switched off)</li>
                            <li>3. Continue holding the test print button and switch printer power to 'on' (hold until test page is printed)</li>
                            <li>4. Identify the IP address on the test print page and keep track for later use</li>
                          </ol>
                          
                          <div className="bg-amber-50 p-3 rounded border mt-4">
                            <p className="text-sm text-amber-700">
                              <strong>Note:</strong> If no test page prints or no IP address can be found, follow these troubleshooting steps or opt for the remote access option to let our team handle the technical details for you
                            </p>
                          </div>

                          <Accordion type="single" collapsible className="w-full mt-4">
                            <AccordionItem value="troubleshooting">
                              <AccordionTrigger className="text-left">
                                <span className="font-medium flex items-center gap-2">
                                  <AlertTriangle className="h-4 w-4" />
                                  Troubleshooting Steps
                                </span>
                              </AccordionTrigger>
                              <AccordionContent className="space-y-3">
                                <div className="space-y-2 text-sm">
                                  <p><strong>Troubleshooting step 1:</strong> Double check that the printer is not connected via USB - if it is, move to the USB Connection guide</p>
                                  <p><strong>Troubleshooting step 2:</strong> Some POS systems have hidden printer IP addresses (such as certain models of SwiftPOS). If you're confident that the printer is Network Connected, check the backoffice of your POS system for printer settings and locate the IP address there.</p>
                                </div>
                                <div className="bg-blue-50 p-3 rounded border">
                                  <p className="text-sm text-blue-700">
                                    <strong>Note:</strong> If you still can't locate the IP address of your printer please reach out to our team for help - support@pumpd.co.nz. Alternatively, opt for the Remote Access option.
                                  </p>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 2: Download the PushPrinter Application on your POS device</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              <strong>If you're accessing this dashboard from the POS:</strong>
                              <div className="mt-2">
                                <a 
                                  href="https://pushprinter-v3-releases.s3.amazonaws.com/PushPrinter+Setup+3.0.0.exe" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Download PushPrinter
                                </a>
                              </div>
                            </li>
                            <li>
                              <strong>Accessing this dashboard from another device?</strong> Simply open up a web browser on the POS device and navigate to <a href="https://pushprinter.com/#windows" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://pushprinter.com/#windows</a>. Then, click 'Download'
                            </li>
                            <li>Once the application is downloaded, open 'downloads' in the web browser and click on the downloaded file. Click 'Allow application to make changes to this device' and complete the installation.</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 3: Setup the virtual printer on the Pump'd Admin Dashboard</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              1. Navigate to your admin dashboard here:
                              <div className="mt-2">
                                <a 
                                  href="https://admin.pumpd.co.nz" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Admin Dashboard
                                </a>
                              </div>
                            </li>
                            <li>2. Login with the Temporary Password sent to you by your account manager</li>
                            <li>3. Navigate to Settings → System → Receipt Printing and click 'Create Printer'</li>
                            <li>4. Give the printer a name like "Receipts" or "Kitchen Dockets"</li>
                            <li>5. Toggle 'Auto Print Orders' or 'Auto Print Orders (Confirmed Only) to 'On'</li>
                            <li>6. Scroll down to click 'Save'</li>
                            <li>7. Click the API key to copy it to the clipboard and note it down for later use.</li>
                          </ol>
                          <div className="bg-blue-50 p-3 rounded border mt-3">
                            <p className="text-sm text-blue-700">
                              <strong>Note:</strong> Accessing this dashboard from a different device than the POS? Send yourself an email with the API key to ensure it is copied correctly in the next step
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-4">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 4: Connect the printer to Pump'd by configuring the PushPrinter App</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-2 text-sm">
                            <li>1. Open the PushPrinter App (Previously downloaded and installed)</li>
                            <li>2. Navigate to 'Settings' and enable 'Automatically start Pushprinter' to ensure the application starts when the POS is turned off or restarted</li>
                            <li>3. Navigate to 'Printers' and click 'Create Printer'</li>
                            <li>4. Give the printer the same name as you named the virtual printer on Pump'd Admin (Not necessary but good for keeping track)</li>
                            <li>5. Copy and paste your API key from the Pump'd Admin settings</li>
                            <li>6. Ensure the Printing Type is 'Network (ESCPOS)'</li>
                            <li>7. Add the Printer IP Address (Noted down earlier) and configure the port to '9100'</li>
                            <li>8. Optionally modify the Number Of Copies</li>
                            <li>9. Click 'Send Test Print' to check the device is configured correctly</li>
                            <li>10. Scroll down and click 'Edit Printer' to save</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </TabsContent>

              <TabsContent value="usb" className="space-y-4 mt-6">
                <div className="space-y-4">
                  <h4 className="font-medium">USB Connection Setup Steps:</h4>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="step-1">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 1: Setup USB printer</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <div className="bg-amber-50 p-4 rounded border">
                            <h6 className="font-medium mb-2 text-amber-900">Important Note:</h6>
                            <p className="text-sm text-amber-700 mb-3">
                              Only complete this once drivers have been installed. If you're using a printer you already own and drivers have not yet been installed, we recommend opting for the Remote Access option or purchasing one of our Network/Bluetooth enabled printers. However, if you're very tech-savvy - Install our printer drivers by clicking here:
                            </p>
                            <div className="mb-3">
                              <a 
                                href="https://1drv.ms/u/s!AmhcBs25QESrhcRP-YnDtkonagQXWQ?e=KRUfcp" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                              >
                                <ExternalLink className="h-4 w-4" />
                                Install Printer Drivers
                              </a>
                            </div>
                            <p className="text-sm text-amber-700">
                              If the USB printer already has drivers installed or you've successfully installed them, the printer should become visible in the Devices and Printers section, so please move on to the next step, enabling Windows shared printing.
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 2: Enable Windows shared printing</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-2 text-sm">
                            <li>1. Open Windows Control Panel from the windows search bar</li>
                            <li>2. Open 'Devices and Printers'</li>
                            <li>3. Right click the printer you want to connect to and select 'Printer Properties'</li>
                            <li>4. To test that the correct printer is selected, press 'Print Test Page'. (If the test page does not print, please reach out to our team for help - support@pumpd.co.nz)</li>
                            <li>5. Toggle the 'Share this printer' checkbox to 'Yes' and click 'Apply'</li>
                            <li>6. Note the name of the printer for later use</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 3: Download the PushPrinter Application on your POS device</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              <strong>If you're accessing this dashboard from the POS:</strong>
                              <div className="mt-2">
                                <a 
                                  href="https://pushprinter-v3-releases.s3.amazonaws.com/PushPrinter+Setup+3.0.0.exe" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Download PushPrinter
                                </a>
                              </div>
                            </li>
                            <li>
                              <strong>Accessing this dashboard from another device?</strong> Simply open up a web browser on the POS device and navigate to <a href="https://pushprinter.com/#windows" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://pushprinter.com/#windows</a>. Then, click 'Download'
                            </li>
                            <li>Once the application is downloaded, open 'downloads' in the web browser and click on the downloaded file. Click 'Allow application to make changes to this device' and complete the installation.</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-4">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 4: Setup the virtual printer on the Pump'd Admin Dashboard</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              1. Navigate to your admin dashboard here:
                              <div className="mt-2">
                                <a 
                                  href="https://admin.pumpd.co.nz" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Admin Dashboard
                                </a>
                              </div>
                            </li>
                            <li>2. Login with the Temporary Password sent to you by your account manager</li>
                            <li>3. Navigate to Settings → System → Receipt Printing and click 'Create Printer'</li>
                            <li>4. Give the printer a name like "Receipts" or "Kitchen Dockets"</li>
                            <li>5. Toggle 'Auto Print Orders' or 'Auto Print Orders (Confirmed Only) to 'On'</li>
                            <li>6. Scroll down to click 'Save'</li>
                            <li>7. Click the API key to copy it to the clipboard and note it down for later use.</li>
                          </ol>
                          <div className="bg-blue-50 p-3 rounded border mt-3">
                            <p className="text-sm text-blue-700">
                              <strong>Note:</strong> Accessing this dashboard from a different device than the POS? Send yourself an email with the API key to ensure it is copied correctly in the next step
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-5">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 5: Connect the printer to Pump'd by configuring the PushPrinter App</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-2 text-sm">
                            <li>1. Open the PushPrinter App (Previously downloaded and installed)</li>
                            <li>2. Navigate to 'Settings' and enable 'Automatically start Pushprinter' to ensure the application starts when the POS is turned off or restarted</li>
                            <li>3. Navigate to 'Printers' and click 'Create Printer'</li>
                            <li>4. Give the printer the same name as you named the virtual printer on Pump'd Admin (Not necessary but good for keeping track)</li>
                            <li>5. Copy and paste your API key from the Pump'd Admin settings</li>
                            <li>6. Ensure the Printing Type is 'Windows Shared Printer (ESCPOS)'</li>
                            <li>7. Add the Windows Shared Printer Name exactly as it is named in the windows shared printer settings <Badge variant="destructive" className="ml-2">Important</Badge></li>
                            <li>8. Optionally modify the Number Of Copies</li>
                            <li>9. Click 'Send Test Print' to check the device is configured correctly</li>
                            <li>10. Click 'Edit Printer' to save</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>

        <TabsContent value="tablet" className="space-y-6 mt-6">
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg border">
              <h5 className="font-medium text-purple-900 mb-2">Best for:</h5>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• Restaurants with existing iOS or Android based POS systems</li>
                <li>• Popups & Foodtrucks</li>
                <li>• Existing Bluetooth Connected Printers</li>
              </ul>
            </div>

            <Tabs defaultValue="network" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="network" className="flex items-center gap-2 data-[state=active]:text-white group">
                  <Wifi className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                  Network/Ethernet (Recommended)
                </TabsTrigger>
                <TabsTrigger value="bluetooth" className="flex items-center gap-2 data-[state=active]:text-white group">
                  <Bluetooth className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                  Bluetooth
                </TabsTrigger>
              </TabsList>

              <TabsContent value="network" className="space-y-4 mt-6">
                <div className="space-y-4">
                  <h4 className="font-medium">Network Setup Steps:</h4>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="step-1">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 1: Download the PushPrinter Android application from Google Play Store</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              <strong>If you're accessing this dashboard from the tablet you want to connect:</strong>
                              <div className="mt-2">
                                <a 
                                  href="https://play.google.com/store/apps/details?id=com.cloudwaitress.pushprinter" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Download PushPrinter from Google Play
                                </a>
                              </div>
                            </li>
                            <li>
                              <strong>Accessing this dashboard from another device?</strong> Simply open up the Google Play Store and type <strong>PushPrinter</strong>, then click "Install"
                              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                                <p className="text-sm text-amber-700">
                                  <strong>Note:</strong> The PushPrinter Application doesn't have an IOS version at this time. We know this isn't ideal, but if you're using an IOS based POS system, you'll need to get a cheap Android tablet to connect Pump'd to your printers
                                </p>
                              </div>
                            </li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 2: Setup the virtual printer on the Pump'd Admin Dashboard</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              1. Navigate to your admin dashboard here:
                              <div className="mt-2">
                                <a 
                                  href="https://admin.pumpd.co.nz" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Admin Dashboard
                                </a>
                              </div>
                            </li>
                            <li>2. Login with the Temporary Password sent to you by your account manager</li>
                            <li>3. Navigate to Settings → System → Receipt Printing and click 'Create Printer'</li>
                            <li>4. Give the printer a name like "Receipts" or "Kitchen Dockets"</li>
                            <li>5. Toggle 'Auto Print Orders' or 'Auto Print Orders (Confirmed Only) to 'On'</li>
                            <li>6. Scroll down to click 'Save'</li>
                            <li>7. Click the API key to copy it to the clipboard and note it down for later use.</li>
                          </ol>
                          <div className="bg-blue-50 p-3 rounded border mt-3">
                            <p className="text-sm text-blue-700">
                              <strong>Note:</strong> Accessing this dashboard from a different device than the tablet? Send yourself an email with the API key to ensure it is copied correctly in step 4
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 3: Identify the printer's IP address</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-2 text-sm">
                            <li>1. Switch printer power to 'off'</li>
                            <li>2. Hold test print button (While device is switched off)</li>
                            <li>3. Continue holding the test print button and switch printer power to 'on' (hold until test page is printed)</li>
                            <li>4. Identify the IP address on the test print page and keep track for later use</li>
                          </ol>
                          
                          <div className="bg-amber-50 p-3 rounded border mt-4">
                            <p className="text-sm text-amber-700">
                              <strong>Note:</strong> If no test page prints or no IP address can be found, follow these troubleshooting steps or opt for the remote access option to let our team handle the technical details for you
                            </p>
                          </div>

                          <Accordion type="single" collapsible className="w-full mt-4">
                            <AccordionItem value="troubleshooting">
                              <AccordionTrigger className="text-left">
                                <span className="font-medium flex items-center gap-2">
                                  <AlertTriangle className="h-4 w-4" />
                                  Troubleshooting Steps
                                </span>
                              </AccordionTrigger>
                              <AccordionContent className="space-y-3">
                                <div className="space-y-2 text-sm">
                                  <p><strong>Troubleshooting step 1:</strong> Double check that the printer is not connected via USB - if it is, move to the USB Connection guide</p>
                                  <p><strong>Troubleshooting step 2:</strong> Some POS systems have hidden printer IP addresses (such as certain models of SwiftPOS). If you're confident that the printer is Network Connected, check the backoffice of your POS system for printer settings and locate the IP address there.</p>
                                </div>
                                <div className="bg-blue-50 p-3 rounded border">
                                  <p className="text-sm text-blue-700">
                                    <strong>Note:</strong> If you still can't locate the IP address of your printer please reach out to our team for help - support@pumpd.co.nz. Alternatively, opt for the Remote Access option.
                                  </p>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-4">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 4: Connect the printer to Pump'd by configuring the PushPrinter App</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-2 text-sm">
                            <li>1. Open the PushPrinter App and click the "+" icon in the top right corner of the screen</li>
                            <li>2. Select 'Network IP / WIFI'</li>
                            <li>3. Give the printer the same name as you named the virtual printer on Pump'd Admin (Not necessary but good for keeping track)</li>
                            <li>4. Copy and paste your API key from the Pump'd Admin settings</li>
                            <li>5. Add the Printer IP Address (Noted down earlier) and configure the port to '9100'</li>
                            <li>6. Optionally modify the Number Of Copies</li>
                            <li>7. Click 'Send Test Print' to check the device is configured correctly</li>
                            <li>8. Click 'Edit Printer' to save configuration</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </TabsContent>

              <TabsContent value="bluetooth" className="space-y-4 mt-6">
                <div className="space-y-4">
                  <h4 className="font-medium">Bluetooth Setup Steps:</h4>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="step-1">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 1: Download the PushPrinter Android application from Google Play Store</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              <strong>If you're accessing this dashboard from the tablet you want to connect:</strong>
                              <div className="mt-2">
                                <a 
                                  href="https://play.google.com/store/apps/details?id=com.cloudwaitress.pushprinter" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Download PushPrinter from Google Play
                                </a>
                              </div>
                            </li>
                            <li>
                              <strong>Accessing this dashboard from another device?</strong> Simply open up the Google Play Store and type <strong>PushPrinter</strong>, then click "Install"
                              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                                <p className="text-sm text-amber-700">
                                  <strong>Note:</strong> The PushPrinter Application doesn't have an IOS version at this time. We know this isn't ideal, but if you're using an IOS based POS system, you'll need to get a cheap Android tablet to connect Pump'd to your printers
                                </p>
                              </div>
                            </li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 2: Setup the virtual printer on the Pump'd Admin Dashboard</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-3 text-sm">
                            <li>
                              1. Navigate to your admin dashboard here:
                              <div className="mt-2">
                                <a 
                                  href="https://admin.pumpd.co.nz" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm"
                                >
                                  <ExternalLink className="h-4 w-4 stroke-white" />
                                  Admin Dashboard
                                </a>
                              </div>
                            </li>
                            <li>2. Login with the Temporary Password sent to you by your account manager</li>
                            <li>3. Navigate to Settings → System → Receipt Printing and click 'Create Printer'</li>
                            <li>4. Give the printer a name like "Receipts" or "Kitchen Dockets"</li>
                            <li>5. Toggle 'Auto Print Orders' or 'Auto Print Orders (Confirmed Only) to 'On'</li>
                            <li>6. Scroll down to click 'Save'</li>
                            <li>7. Click the API key to copy it to the clipboard and note it down for later use.</li>
                          </ol>
                          <div className="bg-blue-50 p-3 rounded border mt-3">
                            <p className="text-sm text-blue-700">
                              <strong>Note:</strong> Accessing this dashboard from a different device than the tablet? Send yourself an email with the API key to ensure it is copied correctly in the next step
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3">
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">Step 3: Connect the printer to Pump'd by configuring the PushPrinter App</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <div className="prose prose-sm max-w-none">
                          <ol className="space-y-2 text-sm">
                            <li>1. Open the PushPrinter App and click the "+" icon in the top right corner of the screen</li>
                            <li>2. Select 'Bluetooth'</li>
                            <li>3. Restart the Bluetooth enabled printer to enable pairing</li>
                            <li>4. Select the printer from the list of available devices - should be named 'POS-80', 'POS-BW' or something similar</li>
                            <li>5. Enter the default connection pincode (try 0000 or 1234 if unsure)</li>
                            <li>6. Give the printer the same name as you named the virtual printer on Pump'd Admin (Not necessary but good for keeping track)</li>
                            <li>7. Copy and paste your API key from the Pump'd Admin settings</li>
                            <li>8. Optionally modify the Number Of Copies</li>
                            <li>9. Click 'Send Test Print' to check the device is configured correctly</li>
                            <li>10. Click 'Edit Printer' to save configuration</li>
                          </ol>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  // DIY requirements
  const diyRequirements = [
    "Compatible thermal receipt printer (see supported models)",
    "Ethernet cable (for network setup) or USB cable",
    "Administrator access to the computer/POS system",
    "Basic knowledge of Windows printer installation"
  ];

  // Remote access form
  const remoteFormContent = (
    <Form {...form}>
      <div className="space-y-4">
        <Alert>
          <Monitor className="h-4 w-4" />
          <AlertDescription>
            Our team will use TeamViewer to securely access your computer and configure the printer. 
            Please ensure TeamViewer is installed and running before providing these details.
          </AlertDescription>
        </Alert>

        <FormField
          control={form.control}
          name="teamviewer_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>TeamViewer ID</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., 123 456 789"
                  value={field.value || ''}
                />
              </FormControl>
              <FormDescription>
                9-digit ID shown in TeamViewer window
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="teamviewer_password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>TeamViewer Password</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="4-digit password"
                  value={field.value || ''}
                />
              </FormControl>
              <FormDescription>
                Temporary password for this session
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="bg-amber-50 p-3 rounded border">
          <h5 className="font-medium text-amber-900 mb-1">Security Note</h5>
          <p className="text-sm text-amber-700">
            This password is only valid for the current TeamViewer session. 
            Our team will only access your system to configure the printer and will not access any other files or applications.
          </p>
        </div>
      </div>
    </Form>
  );

  const remoteInstructions = [
    <>Download and install TeamViewer from <a href="https://www.teamviewer.com/en-nz/download/windows/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">teamviewer.com</a> if not already installed</>,
    "Start TeamViewer and note down your ID and password",
    "Ensure your printer is connected and powered on",
    "Fill in the TeamViewer details below",
    "Our team will contact you to schedule the remote setup session"
  ];

  return (
    <IntegrationSetup
      title="Printer Setup"
      description="Configure your thermal receipt printer for order processing"
      icon={<Printer className="h-6 w-6 text-primary" />}
      status={sectionStatus}
      setupMethod={currentSetupMethod}
      
      customDiyContent={customDiyContent}
      diyRequirements={diyRequirements}
      
      remoteDescription="Our technical team will remotely configure your printer using TeamViewer, ensuring optimal settings for receipt printing."
      remoteInstructions={remoteInstructions}
      remoteFormContent={remoteFormContent}
      
      onSetupMethodChange={handleSetupMethodChange}
      onSave={handleSave}
      onMarkComplete={handleMarkComplete}
      
      isSaving={isSaving}
      isMarkingComplete={isMarkingComplete}
      hasUnsavedChanges={hasUnsavedChanges}
      canMarkComplete={canMarkComplete()}
    />
  );
};

export default PrinterSetup;